import { Stack, TextField, Typography ,Button, Grid, FormControl, Radio, Paper, IconButton, useTheme, useMediaQuery} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import React, { useEffect, useMemo, useState } from 'react'
import { Cart } from '../../cart/components/Cart'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { addAddressAsync, selectAddressStatus, selectAddresses } from '../../address/AddressSlice'
import { selectLoggedInUser } from '../../auth/AuthSlice'
import { Link, useNavigate } from 'react-router-dom'
import { createOrderAsync, selectCurrentOrder, selectOrderStatus } from '../../order/OrderSlice'
import { resetCartByUserIdAsync, selectCartItems } from '../../cart/CartSlice'
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ONLINE_PAYMENT_ACCOUNTS } from '../../../constants'
import { toast } from 'react-toastify'
import {motion} from 'framer-motion'
import { axiosi } from '../../../config/axios'

const DASHBOARD_OFFER_TOKEN_KEY='nexocart_dashboard_offer_token'
const DASHBOARD_OFFER_DEVICE_KEY='nexocart_dashboard_offer_device_id'

const getDashboardOfferDeviceId=()=>{
    let deviceId=localStorage.getItem(DASHBOARD_OFFER_DEVICE_KEY)
    if(!deviceId){
        deviceId=window.crypto?.randomUUID?.() || `device-${Date.now()}-${Math.random().toString(36).slice(2)}`
        localStorage.setItem(DASHBOARD_OFFER_DEVICE_KEY,deviceId)
    }
    return deviceId
}

export const Checkout = () => {

    const status=''
    const addresses=useSelector(selectAddresses)
    const [selectedAddress,setSelectedAddress]=useState(addresses[0])
    const [selectedPaymentMethod,setSelectedPaymentMethod]=useState('COD')
    const [trxId,setTrxId]=useState("")
    const [paymentProof,setPaymentProof]=useState(null)
    const [paymentSettings,setPaymentSettings]=useState({jazzCashNumber:null,onlinePaymentDiscount:0})
    const [couponCode,setCouponCode]=useState("")
    const [appliedCoupon,setAppliedCoupon]=useState(null)
    const [dashboardOfferToken,setDashboardOfferToken]=useState(()=>localStorage.getItem(DASHBOARD_OFFER_TOKEN_KEY) || "")
    const [dashboardOfferMessage,setDashboardOfferMessage]=useState("")
    const { register, handleSubmit, reset } = useForm()
    const dispatch=useDispatch()
    const loggedInUser=useSelector(selectLoggedInUser)
    const addressStatus=useSelector(selectAddressStatus)
    const navigate=useNavigate()
    const cartItems=useSelector(selectCartItems)
    const orderStatus=useSelector(selectOrderStatus)
    const currentOrder=useSelector(selectCurrentOrder)
    const theme=useTheme()
    const is900=useMediaQuery(theme.breakpoints.down(900))
    const is480=useMediaQuery(theme.breakpoints.down(480))
    const highlightBoxSx = {
        bgcolor:'#e8f5e9',
        border:'1px solid #81c784',
        borderRadius:1,
        px:1.2,
        py:0.6,
        width:'fit-content',
        fontWeight:800,
        color:'#1b5e20',
        letterSpacing:'.02rem'
    }

    const subtotal=useMemo(()=>cartItems.reduce((acc,item)=>(item.product.price*item.quantity)+acc,0),[cartItems])
    const itemCount=useMemo(()=>cartItems.reduce((acc,item)=>acc+(item.quantity || 1),0),[cartItems])
    const deliveryRate=itemCount<=1 ? 300 : itemCount===2 ? 250 : 200
    const productDelivery=itemCount*deliveryRate
    const baseTotal=subtotal+productDelivery
    const couponDiscountAmount=appliedCoupon ? (subtotal*(appliedCoupon.discountPercentage/100)) : 0
    const onlineDiscountAmount=selectedPaymentMethod==='ONLINE' ? (baseTotal*((paymentSettings?.onlinePaymentDiscount||0)/100)) : 0
    const orderTotal=Math.max(baseTotal - couponDiscountAmount - onlineDiscountAmount,0)
    
    useEffect(()=>{
        if(addressStatus==='fulfilled'){
            reset()
        }
        else if(addressStatus==='rejected'){
            alert('Error adding your address')
        }
    },[addressStatus, reset])

    useEffect(()=>{
        if(currentOrder && currentOrder?._id){
            dispatch(resetCartByUserIdAsync(loggedInUser?._id))
            navigate(`/order-success/${currentOrder?._id}`)
        }
    },[currentOrder, dispatch, loggedInUser?._id, navigate])

    useEffect(()=>{
        const fetchSettings=async()=>{
            try{
                const res=await axiosi.get("/settings/payment")
                setPaymentSettings(res.data || {jazzCashNumber:null,onlinePaymentDiscount:0})
            }catch(err){
                console.error("payment setting fetch",err)
            }
        }
        fetchSettings()
    },[])

    useEffect(()=>{
        const validateDashboardOffer=async()=>{
            if(!dashboardOfferToken){
                return
            }
            try{
                const res=await axiosi.post("/coupons/dashboard-offer/validate",{
                    offerToken:dashboardOfferToken,
                    deviceId:getDashboardOfferDeviceId(),
                })
                setAppliedCoupon({...res.data,isDashboardOffer:true})
                setCouponCode(res.data.code)
                setDashboardOfferMessage("Nexocart member offer applied: 10% extra off. This offer is locked to this device.")
            }catch(err){
                localStorage.removeItem(DASHBOARD_OFFER_TOKEN_KEY)
                setDashboardOfferToken("")
                setDashboardOfferMessage(err?.response?.data?.message || "Nexocart offer could not be applied.")
            }
        }
        validateDashboardOffer()
    },[dashboardOfferToken])
    
    const handleAddAddress=(data)=>{
        const address={...data,user:loggedInUser._id}
        dispatch(addAddressAsync(address))
    }

    const fileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => resolve(reader.result)
            reader.onerror = (error) => reject(error)
        })

    const handleCreateOrder=async()=>{
        if(cartItems.length===0){
            toast.error("Your cart is empty")
            return
        }

        const condensedItems = cartItems.map((item)=>({
            product:item?.product,
            quantity:item?.quantity || 1,
        }))

        if(!selectedAddress){
            toast.error("Please select a shipping address")
            return
        }

        if(selectedPaymentMethod==='ONLINE'){
            if(!trxId){
                toast.error("Please enter TRX / transaction id")
                return
            }
            if(!paymentProof){
                toast.error("Please attach payment screenshot")
                return
            }
        }

        const deliveryAddress = {
            name:selectedAddress?.name || loggedInUser?.name || '',
            type:selectedAddress?.type || '',
            street:selectedAddress?.street || '',
            city:selectedAddress?.city || '',
            state:selectedAddress?.state || '',
            phoneNumber:selectedAddress?.phoneNumber || '',
            postalCode:selectedAddress?.postalCode || '',
            country:selectedAddress?.country || '',
            _id:selectedAddress?._id,
        }

        const paymentProofBase64 = paymentProof ? await fileToBase64(paymentProof) : null

        const order={
            user:loggedInUser._id,
            items:condensedItems,
            item:condensedItems, // keep legacy key for backend compatibility
            address:[deliveryAddress],
            paymentMode:selectedPaymentMethod,
            trxId:selectedPaymentMethod==='ONLINE'?trxId:null,
            paymentProof:paymentProofBase64,
            couponCode:appliedCoupon?.code,
            dashboardOfferToken:appliedCoupon?.isDashboardOffer ? dashboardOfferToken : null,
            dashboardOfferDeviceId:appliedCoupon?.isDashboardOffer ? getDashboardOfferDeviceId() : null,
            couponDiscount:couponDiscountAmount,
            onlinePaymentDiscount:onlineDiscountAmount,
            deliveryCharges:productDelivery,
            subtotal:subtotal,
            total:orderTotal
        }
        try{
            await dispatch(createOrderAsync(order)).unwrap()
        }catch(err){
            toast.error(err?.message || "Error creating an order, please try again later")
        }
    }

    const handleApplyCoupon=async()=>{
        if(!couponCode){toast.error("Enter coupon code");return}
        if(appliedCoupon?.isDashboardOffer){
            toast.info("Your Nexocart 10% offer is already applied")
            return
        }
        try{
            const res=await axiosi.post("/coupons/validate",{code:couponCode})
            setAppliedCoupon(res.data)
            setDashboardOfferMessage("")
            toast.success("Coupon applied")
        }catch(err){
            toast.error(err?.response?.data?.message || "Invalid coupon")
        }
    }

  return (
    <Stack flexDirection={'row'} p={2} rowGap={10} justifyContent={'center'} flexWrap={'wrap'} mb={'5rem'} mt={2} columnGap={4} alignItems={'flex-start'}>

        {/* left box */}
        <Stack rowGap={4}>

            {/* heading */}
            <Stack flexDirection={'row'} columnGap={is480?0.3:1} alignItems={'center'}>
                <motion.div  whileHover={{x:-5}}>
                    <IconButton component={Link} to={"/cart"}><ArrowBackIcon fontSize={is480?"medium":'large'}/></IconButton>
                </motion.div>
                <Typography variant='h4'>Shipping Information</Typography>
            </Stack>

            {/* address form */}
            <Stack component={'form'} noValidate rowGap={2} onSubmit={handleSubmit(handleAddAddress)}>
                    <Stack>
                        <Typography  gutterBottom>Name</Typography>
                        <TextField placeholder='Full name for delivery' {...register("name",{required:true})}/>
                    </Stack>

                    <Stack>
                        <Typography  gutterBottom>Type</Typography>
                        <TextField placeholder='Eg. Home, Buisness' {...register("type",{required:true})}/>
                    </Stack>


                    <Stack>
                        <Typography gutterBottom>Street</Typography>
                        <TextField {...register("street",{required:true})}/>
                    </Stack>

                    <Stack>
                        <Typography gutterBottom>Country</Typography>
                        <TextField {...register("country",{required:true})}/>
                    </Stack>

                    <Stack>
                        <Typography  gutterBottom>Phone Number</Typography>
                        <TextField type='number' {...register("phoneNumber",{required:true})}/>
                    </Stack>

                    <Stack flexDirection={'row'}>
                        <Stack width={'100%'}>
                            <Typography gutterBottom>City</Typography>
                            <TextField  {...register("city",{required:true})}/>
                        </Stack>
                        <Stack width={'100%'}>
                            <Typography gutterBottom>State</Typography>
                            <TextField  {...register("state",{required:true})}/>
                        </Stack>
                        <Stack width={'100%'}>
                            <Typography gutterBottom>Postal Code</Typography>
                            <TextField type='number' {...register("postalCode",{required:true})}/>
                        </Stack>
                    </Stack>

                    <Stack flexDirection={'row'} alignSelf={'flex-end'} columnGap={1}>
                        <LoadingButton loading={status==='pending'} type='submit' variant='contained'>add</LoadingButton>
                        <Button color='error' variant='outlined' onClick={()=>reset()}>Reset</Button>
                    </Stack>
            </Stack>

            {/* existing address */}
            <Stack rowGap={3}>

                <Stack>
                    <Typography variant='h6'>Address</Typography>
                    <Typography variant='body2' color={'text.secondary'}>Choose from existing Addresses</Typography>
                </Stack>

                <Grid container gap={2} width={is900?"auto":'50rem'} justifyContent={'flex-start'} alignContent={'flex-start'}>
                        {
                            addresses.map((address,index)=>(
                                <FormControl item key={address._id}>
                                    <Stack
                                        p={is480?2:2}
                                        width={is480?'100%':'20rem'}
                                        height={is480?'auto':'15rem'}
                                        rowGap={2}
                                        component={Paper}
                                        elevation={selectedAddress===address ? 3 : 1}
                                        sx={{
                                            border:'2px solid',
                                            borderColor:selectedAddress===address ? '#1976d2' : '#e0e0e0',
                                            bgcolor:selectedAddress===address ? '#e3f2fd' : '#fff',
                                            transition:'all .2s ease',
                                            cursor:'pointer',
                                            '&:hover':{
                                                borderColor:'#1976d2',
                                                boxShadow:'0 10px 24px rgba(0,0,0,.08)'
                                            }
                                        }}
                                        onClick={()=>setSelectedAddress(addresses[index])}
                                    >

                                        <Stack flexDirection={'row'} alignItems={'center'}>
                                            <Radio checked={selectedAddress===address} name='addressRadioGroup' value={selectedAddress} onChange={()=>setSelectedAddress(addresses[index])}/>
                                            <Typography fontWeight={800}>{address.type}</Typography>
                                            {selectedAddress===address && (
                                                <Typography ml={1} variant='caption' sx={{bgcolor:'#1976d2',color:'#fff',px:1,py:.3,borderRadius:1,fontWeight:700}}>
                                                    Selected
                                                </Typography>
                                            )}
                                        </Stack>

                                        {/* details */}
                                        <Stack rowGap={0.6}>
                                            <Typography fontWeight={800}>{address.name || loggedInUser?.name}</Typography>
                                            <Typography color='text.secondary'>{address.street}</Typography>
                                            <Typography color='text.secondary'>{address.state}, {address.city}, {address.country}, {address.postalCode}</Typography>
                                            <Typography sx={highlightBoxSx}>{address.phoneNumber}</Typography>
                                        </Stack>
                                    </Stack>
                                </FormControl>
                            ))
                        }
                </Grid>

            </Stack>
            
            {/* payment methods */}
            <Stack rowGap={3}>

                    <Stack>
                        <Typography variant='h6'>Payment Methods</Typography>
                        <Typography variant='body2' color={'text.secondary'}>Please select a payment method</Typography>
                    </Stack>
                    
                    <Stack rowGap={2}>

                        <Stack flexDirection={'row'} justifyContent={'flex-start'} alignItems={'center'}>
                            <Radio value={selectedPaymentMethod} name='paymentMethod' checked={selectedPaymentMethod==='COD'} onChange={()=>setSelectedPaymentMethod('COD')}/>
                            <Typography>Cash on Delivery</Typography>
                        </Stack>

                        <Stack flexDirection={'row'} justifyContent={'flex-start'} alignItems={'center'}>
                            <Radio value={selectedPaymentMethod} name='paymentMethod' checked={selectedPaymentMethod==='ONLINE'} onChange={()=>setSelectedPaymentMethod('ONLINE')}/>
                            <Typography>Online Payment</Typography>
                        </Stack>

                        {
                            selectedPaymentMethod==='ONLINE' && (
                                <Stack rowGap={1.5} p={2} borderRadius={2} border={'1px solid #90caf9'} bgcolor={'#f4fbff'}>
                                    <Typography variant='body2' color='text.secondary'>
                                        Pay via bank transfer or EasyPaisa using the details below, then enter your transaction ID and attach a screenshot.
                                    </Typography>

                                    <Stack rowGap={1} p={1.8} bgcolor='#e3f2fd' color='#0d47a1' borderRadius={1.5} border={'1px solid #90caf9'}>
                                        <Typography variant='subtitle2' fontWeight={900}>HBL Bank Transfer</Typography>
                                        <Typography variant='body2'>Bank Name: {ONLINE_PAYMENT_ACCOUNTS.hbl.bankName}</Typography>
                                        <Typography variant='body2'>Account Title: {ONLINE_PAYMENT_ACCOUNTS.hbl.accountTitle}</Typography>
                                        <Stack>
                                            <Typography variant='caption' color='text.secondary'>Account Number</Typography>
                                            <Typography sx={{...highlightBoxSx,bgcolor:'#fff',borderColor:'#1976d2',color:'#0d47a1',fontSize:{xs:'1rem',md:'1.15rem'}}}>
                                                {ONLINE_PAYMENT_ACCOUNTS.hbl.accountNumber}
                                            </Typography>
                                        </Stack>
                                    </Stack>

                                    <Stack rowGap={1} p={1.8} bgcolor='#e8f5e9' borderRadius={1.5} border={'1px solid #a5d6a7'}>
                                        <Typography variant='subtitle2' fontWeight={900} color='#1b5e20'>EasyPaisa</Typography>
                                        <Typography variant='body2'>Account Title: {ONLINE_PAYMENT_ACCOUNTS.easyPaisa.accountTitle}</Typography>
                                        <Stack>
                                            <Typography variant='caption' color='text.secondary'>EasyPaisa Number</Typography>
                                            <Typography sx={{...highlightBoxSx,bgcolor:'#fff',borderColor:'#66bb6a',color:'#1b5e20',fontSize:{xs:'1rem',md:'1.15rem'}}}>
                                                {ONLINE_PAYMENT_ACCOUNTS.easyPaisa.number}
                                            </Typography>
                                        </Stack>
                                    </Stack>

                                    <TextField label="TRX / Transaction ID" size='small' value={trxId} onChange={(e)=>setTrxId(e.target.value)}/>
                                    <Button variant='outlined' component="label" size='small'>
                                        Attach Screenshot
                                        <input hidden type="file" accept="image/*" onChange={(e)=>setPaymentProof(e.target.files[0]||null)} />
                                    </Button>
                                    <Typography variant='caption' color='text.secondary'>
                                        {paymentProof?.name ? paymentProof.name : 'No file selected'}
                                    </Typography>
                                    {
                                        paymentSettings?.onlinePaymentDiscount>0 && (
                                            <Typography variant='body2' color='success.main'>
                                                Online payment discount: {paymentSettings.onlinePaymentDiscount}% applied
                                            </Typography>
                                        )
                                    }
                                </Stack>
                            )
                        }

                    </Stack>


            </Stack>
        </Stack>

        {/* right box */}
        <Stack  width={is900?'100%':'auto'} alignItems={is900?'flex-start':''}>
            <Typography variant='h4'>Order summary</Typography>
            <Cart checkout={true}/>

            <Stack rowGap={1} mt={2} mb={2}>
                <Typography variant='body1'>Subtotal: Rs. {subtotal.toFixed(2)}</Typography>
                <Typography variant='body1'>Delivery: Rs. {productDelivery.toFixed(2)} ({itemCount} item{itemCount===1?'':'s'} x Rs. {deliveryRate})</Typography>
                <Typography variant='body1'>Coupon discount: -Rs. {couponDiscountAmount.toFixed(2)}</Typography>
                <Typography variant='body1'>Online payment discount: -Rs. {onlineDiscountAmount.toFixed(2)}</Typography>
                <Typography variant='h6'>Total: Rs. {orderTotal.toFixed(2)}</Typography>
                {dashboardOfferMessage && (
                    <Typography variant='body2' color={appliedCoupon?.isDashboardOffer ? 'success.main' : 'error.main'} fontWeight={700}>
                        {dashboardOfferMessage}
                    </Typography>
                )}
            </Stack>

            <Stack direction={'row'} alignItems={'center'} columnGap={1} mb={2}>
                <TextField
                    label="Coupon code"
                    size='small'
                    value={couponCode}
                    onChange={(e)=>setCouponCode(e.target.value)}
                    disabled={appliedCoupon?.isDashboardOffer}
                />
                <Button variant='outlined' onClick={handleApplyCoupon} disabled={appliedCoupon?.isDashboardOffer}>Apply</Button>
            </Stack>

            <LoadingButton fullWidth loading={orderStatus==='pending'} variant='contained' onClick={handleCreateOrder} size='large'>Pay and order</LoadingButton>
        </Stack>

    </Stack>
  )
}
